import { defineField } from 'sanity'
import { bases } from '../../data/routes'
import { slugify } from '../../utils/slugify'

// TODO: Consider adding slug plugin for better UX
// import SlugInput from 'sanity-plugin-better-slug'

export const seoGroup = {
  name: 'seo',
  title: 'SEO data',
}

// Reserved routes that cannot be used as dynamic page slugs
// These conflict with existing Nuxt routes or system paths
export const RESERVED_ROUTES = [
  // System routes
  'api',
  '_nuxt',
  '__nuxt',
  // Existing page routes (not migrating to dynamic pages)
  'cart',
  'checkout',
  'account',
  'login',
  'register',
  'search',
  'contact-us',
  // Product/catalog routes
  'wheels',
  'accessories',
  'gallery',
  'blog',
  'support',
  'fitment-guides',
  'vehicles',
  // Other system paths
  'sitemap.xml',
  'robots.txt',
  'favicon.ico',
]

const getPageInfo = ({ path = '', lockCanonical = false, slugValidation } = {}) => ({
  title: 'Page Info',
  name: 'pageInfo',
  type: 'object',
  group: 'seo',
  fields: [
    defineField({
      title: 'Title',
      name: 'title',
      type: 'string',
      description: 'Meta Title',
    }),
    defineField({
      title: 'Description',
      name: 'description',
      type: 'string',
      description: 'Meta Description',
    }),
    defineField({
      name: 'metaImage',
      title: 'Meta Image',
      type: 'image',
      options: {
        hotspot: true,
      },
    }),
    defineField({
      title: 'Keywords',
      name: 'keywords',
      type: 'string',
      description: 'Meta Keywords',
    }),
    defineField({
      title: 'Canonical URL',
      name: 'canonical',
      type: 'slug',
      readOnly: lockCanonical,
      // TODO: Add SlugInput component for better UX (optional)
      // components: { input: SlugInput },
      options: {
        source: (doc: any, options: any) => options?.parent?.title || doc?.pageInfo?.title,
        basePath: path.length && Object.keys(bases).includes(path) ? bases[path] : '',
        slugify: (input: string) => slugify(input),
        isUnique: slugValidation,
      },
      description: 'URL slug (Generates from Meta Title)',
      validation: (rule) =>
        rule.custom((slug) => {
          if (!slug?.current) return true

          // Check for valid slug format (alphanumeric, hyphens, slashes)
          if (!/^[a-z0-9]+(?:[-/][a-z0-9]+)*$/.test(slug.current)) {
            return 'Slug can only contain lowercase letters, numbers, hyphens, and slashes'
          }

          // Check for reserved routes. Locked canonicals belong to system-owned
          // singletons (e.g. the Contact page at /contact-us) that intentionally
          // sit on a reserved route, so they must be exempt from this guard.
          if (!lockCanonical) {
            const firstSegment = slug.current.split('/')[0]
            if (RESERVED_ROUTES.includes(firstSegment)) {
              return `"${firstSegment}" is a reserved route and cannot be used as a page slug`
            }
          }

          return true
        }),
    }),
    defineField({
      title: 'Breadcrumbs',
      name: 'breadcrumbs',
      type: 'array',
      of: [
        {
          title: 'Breadcrumb Item',
          name: 'item',
          type: 'object',
          fields: [
            defineField({
              title: 'Label',
              name: 'label',
              type: 'string',
              validation: (rule) => rule.required(),
            }),
            defineField({
              title: 'URL',
              name: 'url',
              type: 'string',
              description:
                'Can be either a path (e.g. /about) or a full URL (e.g. https://apexwheels.com/about). The last item should not have a URL.',
            }),
          ],
          preview: {
            select: {
              label: 'label',
              url: 'url',
            },
            prepare({ label, url }) {
              return {
                title: label,
                subtitle: url,
              }
            },
          },
        },
      ],
    }),
    defineField({
      title: 'No index',
      name: 'noindex',
      type: 'boolean',
      initialValue: false,
      description: 'Prevent this page from showing in search engine results',
    }),
    defineField({
      title: 'No follow',
      name: 'nofollow',
      type: 'boolean',
      initialValue: false,
      description:
        'Prevent search engine to associate links on this page with our site.',
    }),
  ],
})

export default getPageInfo
